import { CircleCheck, Loader, RefreshCw, Undo2, WifiOff } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import { useScreenOwnsLink } from '@krp/client-runtime'
import { Button, StatusMessage, useReloadBlock } from '@krp/ui-kit'

import { QueryState } from './QueryState'
import type { SaleActions, SaleVm } from '../port/types'
import { CartTable } from './CartTable'
import { CatalogDialog } from './CatalogDialog'
import { Checkout, CheckoutDock, CheckoutStatus, type CheckoutTill } from './Checkout'
import { HeldCartsDialog } from './HeldCartsDialog'
import { LineEditor } from './LineEditor'
import { PaymentWindow } from './PaymentWindow'
import { RmkDialog } from './RmkDialog'
import { useReportSaleActivity } from './saleActivity'
import { SaleLinkStrip } from './SaleLink'
import { SearchBar } from './SearchBar'
import { useFillHeight, useNarrow } from './layout'
import './base.css'
import './sale.css'
import './rmk-sale.css'

/** Поле, в которое человек сам печатает (цена, количество, контакт): его нажатия строка поиска не перехватывает. */
const NOT_TEXT_INPUTS = new Set(['button', 'checkbox', 'radio', 'submit', 'reset', 'file', 'image', 'range', 'color'])

function editable(element: Element | null): boolean {
  if (element instanceof HTMLTextAreaElement || element instanceof HTMLSelectElement) return true
  if (element instanceof HTMLInputElement) return !NOT_TEXT_INPUTS.has(element.type)
  return element instanceof HTMLElement && element.isContentEditable
}

/**
 * Экран продажи РМК после открытия смены — как в старом РМК (решение владельца
 * 09.10.2026): экран без прокрутки; слева строка поиска, полоса правки
 * позиции и таблица чека (прокручивается только она), справа колонка расчёта
 * 280–320 px; узкое окно — одна колонка, оплата в ряд; телефон — строки
 * карточками, итог и оплата у нижнего края. Продажа живёт на сервере: после F5
 * окно читает её, а не память.
 */
export function SaleScreen({ vm, actions, till }: { vm: SaleVm; actions: SaleActions; till: CheckoutTill }) {
  const root = useRef<HTMLElement>(null)
  const search = useRef<HTMLInputElement>(null)
  const phone = useNarrow()
  // Телефон тоже меряет высоту: экран тянется хотя бы до нижнего края, и итог с оплатой стоят внизу и в пустом чеке.
  useFillHeight(root, true)
  const [catalogOpen, setCatalogOpen] = useState(false)
  const [heldOpen, setHeldOpen] = useState(false)
  const [clearing, setClearing] = useState(false)
  const [contactOpen, setContactOpen] = useState(false)
  // Окно оплаты — одно на попытку (RP-PAY-ONE): этап, «Свернуть», отказ и тихая отмена решает источник, экран только рисует.
  const payment = vm.payment
  // Открыт чек или идёт оплата — обновление клиента страницу сама не перезагружает (FB43).
  useReloadBlock(vm.lines.length > 0 || payment !== null)
  // RP-MNT: пульт обновления видит, что на кассе: идёт оплата/пробитие, набирается корзина или она пуста.
  useReportSaleActivity(payment !== null ? 'paying' : vm.lines.length > 0 ? 'cart' : 'idle')

  // Выбор строки и конец правки цены — не открытие и закрытие окна: фокус в строку поиска возвращается сразу (сканер печатает туда).
  const refocus = () => {
    requestAnimationFrame(() => search.current?.focus())
  }
  const closeCatalog = () => setCatalogOpen(false)
  const closeHeld = () => setHeldOpen(false)
  const closeClearing = () => setClearing(false)
  const locked = vm.cartLocked || !vm.placeReady
  // Строка поиска — место ввода кассира: сканер-клавиатура печатает в неё. Фокус нужен каждый раз, когда она становится доступной:
  // экран только открылся (строка выключена, пока читаются смена и касса, поэтому `autoFocus` один раз при монтировании не срабатывает),
  // чек разблокировался, закрылось окно оплаты, каталога, отложенных или «Очистить». Чужое поле ввода (цена, контакт покупателя)
  // фокус не теряет.
  const modalOpen = catalogOpen || heldOpen || clearing || (payment !== null && !payment.collapsed)
  const searchReady = !locked && !modalOpen
  // Связь с сервером показываем сами — строкой здесь и в окнах поверх; общее окно по центру их кнопок не перекрывает (RP-SALE-FAST, К-107).
  useScreenOwnsLink()
  useEffect(() => {
    if (!searchReady) return
    const active = document.activeElement
    const typing = active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement || active instanceof HTMLSelectElement
    if (active !== search.current && !typing) search.current?.focus()
  }, [searchReady])
  // К-116: сканер-клавиатура не умеет «нажать на строку поиска» — он печатает туда, где фокус. Фокус уходит от любого нажатия мышью
  // (строка чека, пустое место, кнопка), и скан пропадал, пока кассир не щёлкнет по строке. Поэтому символ, пришедший мимо полей ввода и не
  // поверх другого окна, сам переводит фокус в строку поиска до того, как браузер его напечатает: первый же символ скана — уже в строке.
  useEffect(() => {
    if (!searchReady) return undefined
    const onKeyDown = (event: KeyboardEvent) => {
      const input = search.current
      if (input === null || event.defaultPrevented || event.isComposing || event.ctrlKey || event.metaKey || event.altKey) return
      // Только печатаемый символ: Enter, Tab, Esc и стрелки остаются у того, на чём фокус; пробел нажимает кнопку, а в штрихкоде его нет.
      if (event.key.length !== 1 || event.key === ' ') return
      const active = document.activeElement
      if (active === input || editable(active)) return
      // Поверх продажи открыто другое окно (каталог, оплата, окно рабочего места) — нажатие его.
      if (Array.from(document.querySelectorAll('[role="dialog"][aria-modal="true"]')).some((dialog) => !dialog.contains(input))) return
      input.focus()
    }
    document.addEventListener('keydown', onKeyDown, true)
    return () => document.removeEventListener('keydown', onKeyDown, true)
  }, [searchReady])
  const selected = vm.selectedLine
  const saleReady = vm.saleQuery === null || vm.saleQuery.status === 'ready' || vm.saleQuery.status === 'empty'
  const editor =
    selected === null ? null : (
      <LineEditor
        key={selected.key}
        line={selected}
        locked={locked}
        canEditPrice={vm.canEditPrice}
        onPrice={(value) => actions.setPrice(selected.key, value)}
        onResetPrice={() => actions.resetPrice(selected.key)}
        onPriceDone={refocus}
        onQuantity={(value) => actions.setQuantity(selected.key, value)}
        onIncrement={() => actions.increment(selected.key)}
        onDecrement={() => actions.decrement(selected.key)}
        onRemove={() => actions.removeLine(selected.key)}
      />
    )

  return (
    <section ref={root} className="rmk-sale" aria-label="Продажа" data-phone={phone || undefined}>
      <div className="rmk-sale__screen">
        <div className="rmk-sale__main">
          {!modalOpen && <SaleLinkStrip />}
          <SearchBar
            search={vm.search}
            locked={locked}
            inputRef={search}
            onChange={actions.searchTextChanged}
            onSubmit={actions.submitSearch}
            onPick={(row) => {
              actions.addRow(row)
              refocus()
            }}
            onCatalog={() => setCatalogOpen(true)}
            onScanner={actions.openScanner}
            {...(actions.cancelLookup !== undefined ? { onCancelLookup: actions.cancelLookup } : {})}
            compact={phone}
          />

          {vm.activeRefund !== null && (
            <div className="rmk-sale__busy" role="alert" aria-label="На кассе идёт возврат">
              <p>
                <Undo2 size={16} aria-hidden="true" />{' '}
                {vm.activeRefund.receiptId === ''
                  ? 'На кассе идёт возврат по слипу без чека'
                  : `На кассе идёт возврат по чеку № ${vm.activeRefund.receiptId}`}
                : продажу можно начать, когда он будет завершён или отменён.
              </p>
              <div className="rmk-sale__busy-actions">
                {actions.openRefund !== undefined && (
                  <Button variant="primary" icon={<Undo2 size={14} aria-hidden="true" />} onClick={actions.openRefund}>
                    Открыть возврат
                  </Button>
                )}
                <Button icon={<RefreshCw size={14} aria-hidden="true" />} onClick={actions.reload}>
                  Проверить кассу
                </Button>
              </div>
            </div>
          )}

          {selected !== null && !phone && editor}

          <div className="rmk-sale__scroll">
            {!saleReady && vm.saleQuery !== null ? (
              <QueryState requestIdHidden query={vm.saleQuery} what="Продажа кассы" emptyText="Продажи нет" onRetry={actions.reload} />
            ) : vm.lines.length === 0 ? (
              <StatusMessage
                kind="empty"
                title="Начните продажу"
                text="Отсканируйте штрихкод, введите название или откройте «☰ Каталог»."
              />
            ) : (
              <CartTable
                lines={vm.lines}
                locked={locked}
                canEditPrice={vm.canEditPrice}
                onSelect={(key) => {
                  actions.selectLine(key)
                  refocus()
                }}
                onPriceFocus={actions.selectLine}
                onPrice={actions.setPrice}
                onPriceDone={refocus}
                onIncrement={actions.increment}
                onDecrement={actions.decrement}
                onRemove={actions.removeLine}
                editor={phone && editor !== null ? editor : undefined}
              />
            )}
          </div>

          <div className="rmk-sale__foot">
            <div className="rmk-sale__foot-start">
              {vm.lines.length > 0 && (
                <p className="rmk-sale__totals">
                  {vm.linesText}
                  {vm.quantityTotalText !== null && <> · количество: <b>{vm.quantityTotalText}</b></>}
                </p>
              )}
              {vm.cartSync != null && (
                <p className="rmk-sale__sync" role="status" data-state={vm.cartSync.state}>
                  {vm.cartSync.state === 'saved' ? (
                    <CircleCheck size={14} aria-hidden="true" />
                  ) : vm.cartSync.state === 'saving' ? (
                    <Loader size={14} aria-hidden="true" />
                  ) : (
                    <WifiOff size={14} aria-hidden="true" />
                  )}{' '}
                  {vm.cartSync.text}
                </p>
              )}
              {vm.cartState === 'retrying' && actions.retryCartSave !== undefined && (
                <Button variant="secondary" icon={<RefreshCw size={14} aria-hidden="true" />} onClick={actions.retryCartSave}>
                  Повторить сейчас
                </Button>
              )}
              {(vm.cartState === 'rejected' || vm.cartState === 'retrying') && (
                <Button variant="ghost" icon={<Undo2 size={14} aria-hidden="true" />} onClick={actions.discardDraft}>
                  Вернуть как на сервере
                </Button>
              )}
            </div>
            <CheckoutStatus
              vm={vm}
              till={till}
              onShowPayment={actions.expandPayment}
              onOpenParked={actions.openParked}
              onNoticeAction={actions.noticeAction}
              onNoticeDismiss={actions.noticeDismiss}
            />
          </div>
        </div>

        <Checkout
          vm={vm}
          till={till}
          contactOpen={contactOpen}
          dock={phone}
          onToggleContact={() => setContactOpen((open) => !open)}
          onContact={actions.contactChanged}
          onHold={actions.hold}
          onClear={() => setClearing(true)}
          onHeld={() => setHeldOpen(true)}
          onPay={actions.payWith}
        />
      </div>
      {phone && <CheckoutDock vm={vm} till={till} onPay={actions.payWith} />}

      {catalogOpen && (
        <CatalogDialog
          picker={vm.picker}
          locked={locked}
          onSearch={actions.searchChanged}
          onPage={actions.pageChanged}
          onAdd={actions.addRow}
          onRetry={actions.reload}
          onClose={closeCatalog}
        />
      )}
      {heldOpen && (
        <HeldCartsDialog
          held={vm.held}
          busy={vm.command !== null}
          onRestore={(id) => {
            actions.restoreHeld(id)
            setHeldOpen(false)
          }}
          onDrop={actions.dropHeld}
          onRetry={actions.reload}
          onClose={closeHeld}
        />
      )}
      {clearing && (
        <RmkDialog title="Очистить чек?" size="sm" onClose={closeClearing}>
          <p>Чек очистится целиком. Он ещё не пробит — в кассу ничего не уходило.</p>
          <div className="rmk-dialog__actions">
            <Button
              variant="danger"
              onClick={() => {
                actions.clear()
                closeClearing()
              }}
            >
              Очистить
            </Button>
            <Button variant="ghost" onClick={closeClearing}>
              Отмена
            </Button>
          </div>
        </RmkDialog>
      )}
      {payment !== null && !payment.collapsed && (
        // Ключ — попытка оплаты, а не этап: при смене этапа окно не закрывается и не открывается заново (RP-PAY-ONE).
        <PaymentWindow key={`${vm.resourceId}:${payment.attemptKey}`} payment={payment} vm={vm} actions={actions} />
      )}
    </section>
  )
}
